import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { SimulationSettings, SimulationSetting } from '../../types/simulation';
import { SimulationSettingEnum } from '../../types/simulation';

type SimulationControlsProps = {
  settings: SimulationSettings;
  onSettingChange: (setting: SimulationSetting, value: string) => void;
};

const SETTING_LABELS = {
  [SimulationSettingEnum.SMS_CONNECTION]: 'SMS Connection',
  [SimulationSettingEnum.CODE_VERIFICATION]: 'Code Verification',
  [SimulationSettingEnum.CONTACT_CREATION]: 'Contact Creation',
  [SimulationSettingEnum.MANUAL_VERIFICATION]: 'Manual Verification',
  [SimulationSettingEnum.APPLICATION_SUBMISSION]: 'Application Submission',
  [SimulationSettingEnum.CONTACT_CENTER]: 'Contact Center Availability',
  [SimulationSettingEnum.CALL_CONNECTION]: 'Connect Call',
  [SimulationSettingEnum.EXPERIAN_VERIFICATION]: 'Experian Verification'
} as const;

const SETTING_OPTIONS = {
  [SimulationSettingEnum.SMS_CONNECTION]: [
    { value: 'success', label: 'Success' },
    { value: 'user_exists', label: 'User Already Exists' },
    { value: 'invalid_phone', label: 'Invalid Phone Number' },
    { value: 'service_unavailable', label: 'Service Unavailable' }
  ],
  [SimulationSettingEnum.CONTACT_CENTER]: [
    { value: 'available', label: 'Available (High Capacity)' },
    { value: 'partial', label: 'Available (Low Capacity)' },
    { value: 'unavailable', label: 'Unavailable' }
  ],
  [SimulationSettingEnum.CODE_VERIFICATION]: [
    { value: 'success', label: 'Success' },
    { value: 'invalid_code', label: 'Invalid Code' },
    { value: 'expired_code', label: 'Expired Code' },
    { value: 'too_many_attempts', label: 'Too Many Attempts' }
  ],
  [SimulationSettingEnum.CONTACT_CREATION]: [
    { value: 'qualified', label: 'Accepted (Tier 1)' },
    { value: 'qualified_tier2', label: 'Accepted (Tier 2)' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'error', label: 'Error' },
    { value: 'error_500', label: 'Server Error (500)' }
  ],
  [SimulationSettingEnum.MANUAL_VERIFICATION]: [
    { value: 'qualified', label: 'Qualified' },
    { value: 'unqualified', label: 'Unqualified' },
    { value: 'not_found', label: 'Not Found' },
    { value: 'invalid_data', label: 'Invalid Data' }
  ],
  [SimulationSettingEnum.APPLICATION_SUBMISSION]: [
    { value: 'success', label: 'Success' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'system_error', label: 'System Error' }
  ],
  [SimulationSettingEnum.CALL_CONNECTION]: [
    { value: 'success', label: 'Success' },
    { value: 'invalid_record', label: 'Invalid Record' },
    { value: 'server_error', label: 'Server Error' }
  ],
  [SimulationSettingEnum.EXPERIAN_VERIFICATION]: [
    { value: 'qualified', label: 'Qualified' },
    { value: 'qualified_tier2', label: 'Qualified (Tier 2)' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'error', label: 'Error' },
    { value: 'error_500', label: 'Server Error (500)' }
  ]
} as const;

export default function SimulationControls({ settings, onSettingChange }: SimulationControlsProps) {
  return (
    <div className="space-y-6">
      <div className="bg-yellow-50 p-4 rounded-lg flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-yellow-600">
          <p className="font-medium mb-1">Simulation Mode Active</p>
          <p>These settings only affect the mock services and are intended for testing different scenarios.</p>
        </div>
      </div>

      <div className="space-y-4">
        {Object.entries(SETTING_LABELS).map(([setting, label]) => (
          <div key={setting} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {label}
            </label>
            <select
              value={settings[setting as SimulationSetting]}
              onChange={(e) => onSettingChange(setting as SimulationSetting, e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#212d52] focus:border-[#212d52] sm:text-sm"
            >
              {SETTING_OPTIONS[setting as SimulationSetting].map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}